import React, { useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

interface ShipStatusTrackerProps {
  upperDeckSections: SectionHP[];
  bottomDeckSections: SectionHP[];
  riggingSections: SectionHP[];
}

interface SectionHP {
  current: number;
  max: number;
}

const ShipStatusTracker: React.FC<ShipStatusTrackerProps> = ({
  upperDeckSections,
  bottomDeckSections,
  riggingSections
}) => {
  const statuses = useMemo(() => {
    const allHullSections = [...upperDeckSections, ...bottomDeckSections];
    const totalHullSections = allHullSections.length;
    const damagedHullSections = allHullSections.filter(section => section.current <= section.max / 2);
    const destroyedHullSections = allHullSections.filter(section => section.current === 0);
    const damagedRiggingSections = riggingSections.filter(section => section.current <= section.max / 2);
    const destroyedRiggingSections = riggingSections.filter(section => section.current === 0);

    const sinkingDC = destroyedHullSections.length > 0 ? 
      15 + (destroyedHullSections.length - 1) * 4 : 0;

    return {
      damaged: damagedHullSections.length > 0 && damagedHullSections.length < 2,
      severelyDamaged: damagedHullSections.length >= 2,
      holed: destroyedHullSections.length > 0,
      demolished: destroyedHullSections.length >= totalHullSections * 0.25,
      riggingDamaged: damagedRiggingSections.length > 0,
      riggingSeverelyDamaged: damagedRiggingSections.length >= 2,
      dismasted: destroyedRiggingSections.length > 0,
      sinkingDC: sinkingDC,
    };
  }, [upperDeckSections, bottomDeckSections, riggingSections]);

  const consequences = [
    statuses.damaged && "Damaged: -5 feet to base speed",
    statuses.severelyDamaged && "Severely Damaged: -10 feet to base speed",
    statuses.holed && `Holed: Sinking check required (DC ${statuses.sinkingDC})`,
    statuses.demolished && "Demolished: Ship sinks immediately",
    statuses.riggingDamaged && "Rigging Damaged: -5 feet to base speed",
    statuses.riggingSeverelyDamaged && "Rigging Severely Damaged: Wind treated as one category lighter",
    statuses.dismasted && riggingSections.length === 1 
      ? "Dismasted (Single Mast): Can no longer sail" 
      : statuses.dismasted && "Dismasted: -10 feet to base speed, -4 to -8 penalty on shiphandling checks"
  ].filter(Boolean);

  return (
    <Card className="mt-4">
      <CardHeader>
        <CardTitle>Ship Status</CardTitle>
      </CardHeader>
      <CardContent>
        <ul className="list-disc pl-5 space-y-2">
          {consequences.map((consequence, index) => (
            <li key={index} className="text-sm">
              {consequence}
            </li>
          ))}
        </ul>
        {consequences.length === 0 && (
          <p className="text-sm italic">Ship is in good condition</p>
        )}
      </CardContent>
    </Card>
  );
};

export default ShipStatusTracker;